import "./style.scss";

import { KeyboardArrowRight } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import LoadingDots from "components/LoadingDots";
import TextSkeleton from "components/shared/TextSkeleton";
import { useTranslation } from "react-i18next";

import type { IWork } from "../types";
import WorkBookStack from "../WorkBookStack";
import { TITLE_TAG_DISPLAY_LIMIT } from "../WorkTitleTags";
import { toneForWorkId } from "../workTone";

type WorkListItemProps = {
    work?: IWork;
    currentWorkId: number | null;
    currentTitleId: number | null;
    isExpanded: boolean;
    titlesCountLabel?: string | null;
    onOpen: (work: IWork) => void;
    onToggleExpand: (work: IWork) => void;
    onOpenTitle: (workId: number, titleId: number) => void;
    isLoading?: boolean;
    skeletonIndex?: number;
    /** Dims other list rows while a title is being edited. */
    editMode?: boolean;
};

const WorkListItem = ({
    work,
    currentWorkId,
    currentTitleId,
    isExpanded,
    titlesCountLabel,
    onOpen,
    onToggleExpand,
    onOpenTitle,
    isLoading = false,
    skeletonIndex = 0,
    editMode = false,
}: WorkListItemProps) => {
    const [t] = useTranslation();
    const titles = work?.titles ?? [];
    const titleCount = titles.length;
    const isWorkActive = work != null && currentWorkId != null && Number(work.id) === Number(currentWorkId);
    const canExpand = !isLoading && titleCount > 0;
    const isWorkDisabled = editMode && work != null && work.id !== currentWorkId;

    return (
        <div
            className={`work-list-item${isWorkActive ? " work-list-item--active active" : ""}${
                isExpanded ? " work-list-item--expanded" : ""
            }${isLoading ? " work-list-item--loading" : ""}${isWorkDisabled ? " work-list-item--disabled" : ""}`}
        >
            <div className="work-list-item__row">
                {canExpand ? (
                    <button
                        type="button"
                        className={`work-list-item__expand${isExpanded ? " work-list-item__expand--open" : ""}`}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? t("Works.CollapseWork") : t("Works.ExpandWork")}
                        disabled={isWorkDisabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (work) onToggleExpand(work);
                        }}
                    >
                        <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </button>
                ) : isLoading ? (
                    <span className="work-list-item__expand work-list-item__expand--loading" aria-hidden>
                        <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </span>
                ) : (
                    <span className="work-list-item__expand-spacer" />
                )}
                <button
                    type="button"
                    className="work-list-item__main"
                    disabled={isLoading || !work || isWorkDisabled}
                    onClick={() => work && onOpen(work)}
                >
                    {isLoading ? (
                        <TextSkeleton variant="rounded" width={36} height={48} />
                    ) : (
                        <WorkBookStack
                            name={work?.name ?? ""}
                            titleCount={titleCount}
                            tone={work ? toneForWorkId(work.id) : "stone"}
                            avatarUrl={work?.avatarUrl}
                        />
                    )}
                    <div className="work-list-item__text">
                        <span className="work-list-item__name">
                            {isLoading ? <TextSkeleton width={`${8 + (skeletonIndex % 4)}em`} /> : work?.name}
                        </span>
                        {!isLoading && work?.workCode?.trim() ? (
                            <span className="work-list-item__code">{work.workCode.trim()}</span>
                        ) : null}
                        <span className="work-list-item__count">
                            {isLoading ? (
                                <TextSkeleton width={`${4 + (skeletonIndex % 3)}em`} />
                            ) : titlesCountLabel ? (
                                titlesCountLabel
                            ) : (
                                <LoadingDots compact />
                            )}
                        </span>
                    </div>
                </button>
            </div>

            {canExpand && (
                <div className="work-list-item__titles-wrap" aria-hidden={!isExpanded}>
                    <ul className="work-list-item__titles">
                        {titles.map((title) => {
                            const isTitleActive = title.id === currentTitleId;
                            const isTitleDisabled = editMode && !isWorkDisabled && title.id !== currentTitleId;
                            return (
                                <li key={title.id}>
                                    <button
                                        type="button"
                                        tabIndex={isExpanded && !isTitleDisabled ? 0 : -1}
                                        className={`work-list-item__title${
                                            isTitleActive ? " work-list-item__title--active" : ""
                                        }${isTitleDisabled ? " work-list-item__title--disabled" : ""}`}
                                        disabled={isTitleDisabled}
                                        onClick={() => work && onOpenTitle(work.id, title.id)}
                                    >
                                        <span className="work-list-item__title-tree" aria-hidden />
                                        <span className="work-list-item__title-copy">
                                            {title.tags && title.tags.length > 0 && (
                                                <span className="work-list-item__title-tags">
                                                    {title.tags.slice(0, TITLE_TAG_DISPLAY_LIMIT).map((tag) => (
                                                        <Tooltip key={tag.id} title={tag.name} arrow>
                                                            <span
                                                                className={`work-list-item__title-tag work-list-item__title-tag--${tag.color}`}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </span>
                                            )}
                                            <span className="work-list-item__title-name">{title.name}</span>
                                            <span className="work-list-item__title-isbn">{title.ean13}</span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default WorkListItem;
